import Dexie, { Table } from 'dexie';
import { Playlist } from '@/types/playlist';

class PlaylistDatabase extends Dexie {
  playlists!: Table<Playlist, string>;

  constructor() {
    super('songkit-playlists');
    this.version(1).stores({
      playlists: 'id, name, createdAt, updatedAt',
    });
  }
}

const db = new PlaylistDatabase();

export const playlistStorage = {
  async getAllPlaylists(): Promise<Playlist[]> {
    return await db.playlists.toArray();
  },

  async getPlaylist(id: string): Promise<Playlist | undefined> {
    return await db.playlists.get(id);
  },

  async createPlaylist(playlist: Playlist): Promise<string> {
    return await db.playlists.add(playlist);
  },

  async updatePlaylist(id: string, updates: Partial<Playlist>): Promise<void> {
    await db.playlists.update(id, {
      ...updates,
      updatedAt: new Date(),
    });
  },

  async deletePlaylist(id: string): Promise<void> {
    await db.playlists.delete(id);
  },

  async exportPlaylists(): Promise<string> {
    const playlists = await db.playlists.toArray();
    return JSON.stringify(playlists, null, 2);
  },

  async importPlaylists(jsonData: string): Promise<number> {
    const playlists: Playlist[] = JSON.parse(jsonData);
    await db.playlists.bulkAdd(playlists);
    return playlists.length;
  },

  async clearAll(): Promise<void> {
    await db.playlists.clear();
  },

  async addTracksToPlaylist(
    playlistId: string,
    tracks: Array<{ songId: number; songName: string; coverArtUrl?: string; revisionId?: number; revisionName?: string }>
  ): Promise<void> {
    const playlist = await db.playlists.get(playlistId);
    if (!playlist) throw new Error('Playlist not found');

    const maxOrder = playlist.songs.length > 0
      ? Math.max(...playlist.songs.map(s => s.order))
      : -1;

    const newTracks = tracks.map((track, index) => ({
      ...track,
      addedAt: new Date(),
      order: maxOrder + index + 1,
    }));

    await db.playlists.update(playlistId, {
      songs: [...playlist.songs, ...newTracks],
      updatedAt: new Date(),
    });
  },

  async removeTrackFromPlaylist(playlistId: string, trackIndex: number): Promise<void> {
    const playlist = await db.playlists.get(playlistId);
    if (!playlist) throw new Error('Playlist not found');

    const updatedSongs = playlist.songs.filter((_, index) => index !== trackIndex);
    // Reorder remaining tracks
    updatedSongs.forEach((song, index) => {
      song.order = index;
    });

    await db.playlists.update(playlistId, {
      songs: updatedSongs,
      updatedAt: new Date(),
    });
  },

  async reorderTracks(playlistId: string, fromIndex: number, toIndex: number): Promise<void> {
    const playlist = await db.playlists.get(playlistId);
    if (!playlist) throw new Error('Playlist not found');

    const songs = [...playlist.songs];
    const [movedSong] = songs.splice(fromIndex, 1);
    songs.splice(toIndex, 0, movedSong);

    // Update order numbers
    songs.forEach((song, index) => {
      song.order = index;
    });

    await db.playlists.update(playlistId, {
      songs,
      updatedAt: new Date(),
    });
  },
};

export default db;
